import moment from "moment";
import {
  buildBillingPath,
  buildStripePath,
} from "../platformAdminNavigation.utils";
import { buildTenantsListPath, DEFAULT_TENANT_FILTERS } from "../Tenants/tenantsList.utils";

export function formatBillingDate(value) {
  if (!value) {
    return "—";
  }

  return moment(value).format("D MMM YYYY");
}

export function formatCurrency(amountCents, currency = "AUD") {
  if (amountCents === null || amountCents === undefined) {
    return "—";
  }

  return new Intl.NumberFormat("en-AU", {
    style: "currency",
    currency: currency || "AUD",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(amountCents / 100);
}

export function formatSubscriptionStatusLabel(status) {
  if (!status || status === "none") {
    return "No Stripe status";
  }

  return String(status).replace(/_/g, " ");
}

export function buildFinancialMetricCards(stats = {}, estimatedMrr = {}, revenueSummary = {}) {
  const data = stats || {};
  const revenue = revenueSummary || {};
  const openInvoices = Number(revenue.open_invoice_count) || 0;
  const failedPayments = Number(data.failed_payments) || 0;

  return [
    {
      id: "collected_all_time",
      label: "Collected (all)",
      value: formatCurrency(revenue.collected_all_time_cents, revenue.currency),
      to: buildStripePath({ tab: "sync", focus: "collected_all_time" }),
      title: "Total collected from synced Stripe invoices",
    },
    {
      id: "outstanding",
      label: "Outstanding",
      value: revenue.outstanding_cents
        ? formatCurrency(revenue.outstanding_cents, revenue.currency)
        : "$0",
      hint: openInvoices ? `${openInvoices} open` : null,
      to: buildBillingPath({ tab: "collections", focus: "overdue" }),
      variant: openInvoices > 0 ? "attention" : "default",
      title: "Review Stripe customers with open invoices",
    },
    {
      id: "collected_last_30_days",
      label: "Collected (30d)",
      value: formatCurrency(revenue.collected_last_30_days_cents, revenue.currency),
      to: buildStripePath({ tab: "sync", focus: "collected_30d" }),
      title: "Stripe invoices collected in the last 30 days",
    },
    {
      id: "estimated_mrr",
      label: "Est. MRR",
      value: estimatedMrr?.available
        ? formatCurrency(estimatedMrr.amount_cents, estimatedMrr.currency)
        : "—",
      to: buildBillingPath({ tab: "plans", focus: "estimated_mrr" }),
      title: "Estimated monthly recurring revenue from Stripe catalog",
    },
    {
      id: "active_subscriptions",
      label: "Paying tenants",
      value: data.active_subscriptions ?? "—",
      to: buildStripePath({ tab: "customers", filter: "paying", focus: "paying_tenants" }),
      variant: "success",
      title: "View paying Stripe customers",
    },
    {
      id: "on_trial",
      label: "On trial",
      value: data.on_trial ?? "—",
      to: buildTenantsListPath({ ...DEFAULT_TENANT_FILTERS, onTrial: true }),
      title: "Open tenants on trial",
    },
    {
      id: "failed_payments",
      label: "Failed payments",
      value: data.failed_payments ?? "—",
      to: buildBillingPath({ focus: "failed_payments", tab: "follow-up" }),
      variant: failedPayments > 0 ? "danger" : "default",
      title: "Jump to failed-payment work queue",
    },
    {
      id: "wildcard_accounts",
      label: "Wildcard",
      value: data.wildcard_accounts ?? "—",
      to: buildBillingPath({ focus: "wildcard_accounts", tab: "follow-up" }),
      title: "Review wildcard billing accounts",
    },
    {
      id: "read_only_billing",
      label: "Read-only billing",
      value: data.read_only_billing ?? "—",
      to: buildTenantsListPath({ ...DEFAULT_TENANT_FILTERS, readOnly: true }),
      title: "Open read-only billing tenants",
    },
  ];
}

export const FINANCIAL_METRIC_GROUPS = [
  {
    id: "revenue",
    title: "Revenue snapshot",
    description: null,
    metricIds: [
      "collected_all_time",
      "collected_last_30_days",
      "outstanding",
      "estimated_mrr",
    ],
  },
  {
    id: "tenants",
    title: "Tenant billing mix",
    description: null,
    metricIds: ["active_subscriptions", "on_trial", "failed_payments"],
  },
  {
    id: "exceptions",
    title: "Billing exceptions",
    description: null,
    metricIds: ["wildcard_accounts", "read_only_billing"],
  },
];

export const RECENT_PAYMENT_FILTERS = [
  { key: "all", label: "All activity" },
  { key: "paid", label: "Paid" },
  { key: "outstanding", label: "Outstanding" },
];

export function filterRecentPayments(payments = [], filterKey = "all") {
  if (filterKey === "paid") {
    return payments.filter((payment) => payment.is_paid);
  }

  if (filterKey === "outstanding") {
    return payments.filter((payment) => !payment.is_paid);
  }

  return payments;
}

export function buildRecentPaymentRows(payments = []) {
  return (payments || []).map((payment) => ({
    ...payment,
    id: payment.id ?? payment.invoice_id,
  }));
}

export const FINANCIAL_OVERVIEW_TAB_IDS = {
  payments: "payments",
  followUp: "follow-up",
  collections: "collections",
  trends: "trends",
  plans: "plans",
};

export const FINANCIAL_OVERVIEW_TABS = [
  { id: FINANCIAL_OVERVIEW_TAB_IDS.payments, label: "Recent payments" },
  { id: FINANCIAL_OVERVIEW_TAB_IDS.followUp, label: "Follow-up" },
  { id: FINANCIAL_OVERVIEW_TAB_IDS.collections, label: "Collections" },
  { id: FINANCIAL_OVERVIEW_TAB_IDS.trends, label: "Trends & charts" },
  { id: FINANCIAL_OVERVIEW_TAB_IDS.plans, label: "Plans & MRR" },
];

export const FINANCIAL_OVERVIEW_SUMMARY_SECTION = "summary";

export const FINANCIAL_OVERVIEW_SECTION_BY_TAB = {
  [FINANCIAL_OVERVIEW_TAB_IDS.payments]: "payments",
  [FINANCIAL_OVERVIEW_TAB_IDS.followUp]: "follow-up",
  [FINANCIAL_OVERVIEW_TAB_IDS.collections]: "collections",
  [FINANCIAL_OVERVIEW_TAB_IDS.trends]: "trends",
  [FINANCIAL_OVERVIEW_TAB_IDS.plans]: "plans",
};

export function resolveCollectionsBadgeCount(billing, collectionsLoaded) {
  if (collectionsLoaded && billing?.collections_summary) {
    return billing.collections_summary.tenant_count ?? 0;
  }

  return billing?.follow_up_counts?.overdue_collections ?? 0;
}

export function resolveFinancialOverviewTab(focus = null) {
  switch (focus) {
    case "failed_payments":
    case "wildcard_accounts":
      return FINANCIAL_OVERVIEW_TAB_IDS.followUp;
    case "overdue":
    case "overdue_collections":
    case "collections":
      return FINANCIAL_OVERVIEW_TAB_IDS.collections;
    case "estimated_mrr":
      return FINANCIAL_OVERVIEW_TAB_IDS.plans;
    case "collected_30d":
    case "collected_last_30_days":
    case "collected_all_time":
    case "outstanding":
      return FINANCIAL_OVERVIEW_TAB_IDS.payments;
    default:
      return FINANCIAL_OVERVIEW_TAB_IDS.payments;
  }
}

export function formatOutstandingSummary(row = {}) {
  const currency = row.currency || "AUD";
  const outstandingCents = row.outstanding_cents ?? 0;
  const openCount = row.open_invoice_count ?? 0;
  const overdueCents = row.overdue_cents ?? 0;
  const overdueCount = row.overdue_invoice_count ?? 0;

  if (outstandingCents <= 0 && overdueCents <= 0) {
    return "—";
  }

  const parts = [];

  if (outstandingCents > 0) {
    parts.push(formatCurrency(outstandingCents, currency));
  }

  if (openCount > 0) {
    parts.push(`${openCount} open`);
  }

  if (overdueCents > 0) {
    parts.push(`${formatCurrency(overdueCents, currency)} overdue (${overdueCount || 1})`);
  }

  return parts.join(" · ");
}

export function buildCollectionsRows(collections = []) {
  return (collections || []).map((row) => ({
    ...row,
    id: row.id,
    tenantId: row.id,
    outstandingLabel: formatOutstandingSummary(row),
    overdueLabel:
      row.overdue_cents > 0
        ? formatCurrency(row.overdue_cents, row.currency || "AUD")
        : "—",
  }));
}

export function buildFinancialMetricGroups(cards = []) {
  const cardMap = new Map((cards || []).map((card) => [card.id, card]));

  return FINANCIAL_METRIC_GROUPS.map((group) => ({
    ...group,
    metrics: group.metricIds
      .map((metricId) => cardMap.get(metricId))
      .filter(Boolean),
  }));
}

export function countFinancialAlertItems(sections = []) {
  return (sections || []).reduce((total, section) => total + (section.rows?.length || 0), 0);
}

export function resolveFollowUpBadgeCount(billing, alertSections, followUpLoaded) {
  if (followUpLoaded) {
    return countFinancialAlertItems(alertSections);
  }

  return billing?.follow_up_counts?.total ?? 0;
}

export function buildPlanBreakdownRows(planBreakdown = []) {
  return (planBreakdown || []).map((row, index) => ({
    id: row.plan_name || index,
    plan_name: row.plan_name || "Unassigned",
    tenant_count: row.tenant_count ?? 0,
    paying_count: row.paying_count ?? 0,
    trial_count: row.trial_count ?? 0,
    failed_count: row.failed_count ?? 0,
    wildcard_count: row.wildcard_count ?? 0,
    estimated_revenue: row.estimated_monthly_revenue_cents
      ? `${row.revenue_estimate_is_fallback ? "~" : ""}${formatCurrency(row.estimated_monthly_revenue_cents, row.currency)}`
      : "—",
  }));
}

export function buildSubscriptionStatusRows(breakdown = []) {
  return (breakdown || []).map((row, index) => ({
    id: row.status || index,
    status: formatSubscriptionStatusLabel(row.status),
    tenant_count: row.tenant_count ?? 0,
  }));
}

export function buildBillingAlertSections(billing) {
  const data = billing ?? {};

  const mapFinancialRow = (row, prefix) => ({
    id: `${prefix}-${row.id}`,
    tenantId: row.id,
    name: row.name,
    outstanding_cents: row.outstanding_cents ?? 0,
    open_invoice_count: row.open_invoice_count ?? 0,
    overdue_cents: row.overdue_cents ?? 0,
    overdue_invoice_count: row.overdue_invoice_count ?? 0,
    currency: row.currency || "AUD",
    read_only: Boolean(row.read_only),
    has_subscription: Boolean(row.has_subscription),
    on_trial: Boolean(row.on_trial),
    trial_end_date: row.trial_end_date,
    outstandingLabel: formatOutstandingSummary(row),
  });

  return [
    {
      id: "trials_expiring",
      title: "Trials expiring (7 days)",
      emptyMessage: "No trials expiring in the next 7 days.",
      borderClass: "border-amber-400",
      rows: (data.trials_expiring || []).map((row) => ({
        ...mapFinancialRow(row, "trial"),
        detail: `Trial ends ${formatBillingDate(row.trial_end_date)}`,
        on_trial: true,
        trial_end_date: row.trial_end_date,
      })),
    },
    {
      id: "failed_payments",
      title: "Failed payments",
      emptyMessage: "No tenants with failed subscription payments.",
      borderClass: "border-red-400",
      rows: (data.failed_payments || []).map((row) => ({
        ...mapFinancialRow(row, "failed"),
        detail: [
          row.subscription_current_status?.replace(/_/g, " ") || "Payment issue",
          row.plan_name,
          formatOutstandingSummary(row) !== "—" ? formatOutstandingSummary(row) : null,
        ]
          .filter(Boolean)
          .join(" · "),
      })),
    },
    {
      id: "overdue_collections",
      title: "Overdue invoices",
      emptyMessage: "No tenants with overdue open invoices.",
      borderClass: "border-rose-500",
      rows: (data.overdue_collections || [])
        .filter((row) => (row.overdue_cents ?? 0) > 0)
        .map((row) => ({
          ...mapFinancialRow(row, "overdue"),
          detail: [
            `${formatCurrency(row.overdue_cents, row.currency || "AUD")} overdue`,
            row.oldest_overdue_at
              ? `since ${formatBillingDate(row.oldest_overdue_at)}`
              : null,
            row.subscription_current_status?.replace(/_/g, " "),
          ]
            .filter(Boolean)
            .join(" · "),
        })),
    },
    {
      id: "participant_limit_risks",
      title: "Participant limit risks",
      emptyMessage: "No tenants over participant plan limits.",
      borderClass: "border-orange-400",
      rows: (data.participant_limit_risks || []).map((row) => ({
        id: `limit-${row.id}`,
        tenantId: row.id,
        name: row.name,
        read_only: Boolean(row.participant_limit_read_only),
        detail: row.participant_limit_read_only
          ? "Read-only due to participant limit"
          : row.participant_limit_exceeded_at
            ? `Exceeded ${formatBillingDate(row.participant_limit_exceeded_at)}`
            : "Over plan participant limit",
      })),
    },
    {
      id: "wildcard_accounts",
      title: "Wildcard accounts",
      emptyMessage: "No wildcard billing accounts.",
      borderClass: "border-indigo-400",
      rows: (data.wildcard_accounts || []).map((row) => ({
        id: `wildcard-${row.id}`,
        tenantId: row.id,
        name: row.name,
        detail: row.wildcard_expiry
          ? `Expires ${formatBillingDate(row.wildcard_expiry)}`
          : "Wildcard billing enabled",
      })),
    },
  ];
}

export function getBillingOverviewSubtitle(computedAt) {
  if (!computedAt) {
    return "Platform financial overview";
  }

  return `Platform financial overview · Updated ${moment(computedAt).fromNow()}`;
}

export function getFinancialOverviewSubtitle(computedAt, revenueSummary = null) {
  const base = getBillingOverviewSubtitle(computedAt);
  const lastSync = revenueSummary?.last_stripe_sync_at;

  if (!lastSync) {
    return base;
  }

  return `${base} · Stripe invoices synced ${moment(lastSync).fromNow()}`;
}

export const FINANCIAL_CHART_COLORS = [
  "#4f46e5",
  "#f59e0b",
  "#ef4444",
  "#10b981",
  "#6366f1",
  "#8b5cf6",
  "#f97316",
  "#9ca3af",
];

export const TENANT_MIX_COLORS = {
  Paying: "#4f46e5",
  "On trial": "#f59e0b",
  Failed: "#ef4444",
  "No subscription": "#9ca3af",
  Wildcard: "#6366f1",
  "Read-only billing": "#f97316",
};

export function buildTenantMixChartData(stats = {}) {
  return [
    { name: "Paying", value: stats.active_subscriptions ?? 0 },
    { name: "On trial", value: stats.on_trial ?? 0 },
    { name: "Failed", value: stats.failed_payments ?? 0 },
    { name: "No subscription", value: stats.no_subscription ?? 0 },
    { name: "Wildcard", value: stats.wildcard_accounts ?? 0 },
    { name: "Read-only billing", value: stats.read_only_billing ?? 0 },
  ].filter((row) => row.value > 0);
}

export function buildSubscriptionStatusChartData(breakdown = []) {
  return (breakdown || [])
    .map((row) => ({
      name: formatSubscriptionStatusLabel(row.status),
      value: row.tenant_count ?? 0,
    }))
    .filter((row) => row.value > 0);
}

export function buildPlanTenantChartData(planBreakdown = []) {
  return (planBreakdown || []).map((row) => ({
    plan: row.plan_name || "Unassigned",
    Paying: row.paying_count ?? 0,
    Trial: row.trial_count ?? 0,
    Failed: row.failed_count ?? 0,
  }));
}

export function buildPlanRevenueChartData(planBreakdown = [], currency = "AUD") {
  return (planBreakdown || [])
    .map((row) => ({
      plan: row.plan_name || "Unassigned",
      revenue_cents: row.estimated_monthly_revenue_cents ?? 0,
      revenue_label: formatCurrency(row.estimated_monthly_revenue_cents, row.currency || currency),
      paying_count: row.paying_count ?? 0,
    }))
    .filter((row) => row.revenue_cents > 0);
}

export function buildTrialExpiryChartData(trialsExpiring = []) {
  const grouped = {};

  (trialsExpiring || []).forEach((row) => {
    const key = row.trial_end_date || "Unknown";
    grouped[key] = (grouped[key] || 0) + 1;
  });

  return Object.entries(grouped)
    .sort(([left], [right]) => String(left).localeCompare(String(right)))
    .map(([date, count]) => ({
      date: formatBillingDate(date),
      count,
    }));
}

export function buildStripeCatalogChartData(catalogSummary = []) {
  return (catalogSummary || []).map((row) => ({
    product: row.product_name,
    monthly_price_cents: row.monthly_price_cents ?? 0,
    monthly_price_label: formatCurrency(row.monthly_price_cents, row.currency),
  }));
}

export function buildRevenueCompositionChartData(stats = {}, estimatedMrr = {}) {
  const paying = stats.active_subscriptions ?? 0;
  const mrrCents = estimatedMrr?.amount_cents ?? 0;

  if (!estimatedMrr?.available || mrrCents <= 0) {
    return [];
  }

  const atRisk = stats.failed_payments ?? 0;
  const trial = stats.on_trial ?? 0;

  return [
    {
      name: "Est. paying MRR",
      value: mrrCents / 100,
      raw_cents: mrrCents,
      kind: "revenue",
    },
    {
      name: "At-risk tenants",
      value: atRisk,
      kind: "count",
    },
    {
      name: "Trial tenants",
      value: trial,
      kind: "count",
    },
  ].filter((row) => row.value > 0);
}

export function buildCollectedTimelineChartData(revenueSummary = {}) {
  return (revenueSummary?.monthly_collected || []).map((row) => ({
    month: row.label || row.month,
    collected_cents: row.collected_cents ?? 0,
    collected_label: formatCurrency(row.collected_cents, revenueSummary.currency),
  }));
}

export function buildRevenueHealthChartData(revenueSummary = {}) {
  const collected = revenueSummary?.collected_last_30_days_cents ?? 0;
  const outstanding = revenueSummary?.outstanding_cents ?? 0;

  return [
    { name: "Collected (30d)", value: collected / 100, raw_cents: collected },
    { name: "Outstanding", value: outstanding / 100, raw_cents: outstanding },
  ].filter((row) => row.raw_cents > 0);
}

export function formatInvoiceStatusLabel(status) {
  if (!status) {
    return "—";
  }

  return String(status).replace(/_/g, " ");
}
